import type { DefaultOptionType } from '@repo/go-ui';
import { Spin, TreeSelect } from 'antd';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import type { ReactElement, CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowIcon from 'src/shared/assets/icons/arrow-drop-icon.svg';

import { hasValue } from '../../../utils/common';
import { showWarningMessage } from '../../../utils/messages';
import { DropdownPreloader } from '../form-select/dropdown-preloader';
import { InputError } from '../input-error';
import { InputLabel } from '../input-label';

import styles from './form-treemultiselect.module.scss';

export type ValidationOptions = Partial<{
  maxCount: number;
}>;

type Props = {
  open?: boolean;
  allowClear?: boolean;
  className?: string;
  placeholder?: string;
  searchValue?: string;
  value?: (string | number)[] | null;
  treeData?: DefaultOptionType[];
  label?: string;
  loading?: boolean;
  showSearch?: boolean;
  filterOption?: boolean;
  errorText?: string | [string, { [locKey: string]: string | number }];
  isDisabled?: boolean;
  dropdownLoading?: boolean;
  customDropdown?: ReactElement;
  dataTestId?: string;
  treeNodeFilterProp?: string;
  dropdownStyle?: CSSProperties;
  onBlur?: () => void;
  onClear?: () => void;
  onFocus?: () => void;
  onSearch?: (value: string) => void;
  onChange?: (value: (string | number)[]) => void;
  onSelect?: (value: string | number) => void;
  onDeselect?: (value: string | number) => void;
  onClick?: () => void;
  onDropdownVisibleChange?: (open: boolean) => void;
  dropdownRender?: (origin: ReactElement) => ReactElement;
  getPopupContainer?: (props: unknown) => HTMLElement;
  validationOptions?: ValidationOptions;
};

export const FormTreeMultiSelect = observer(function FormTreeMultiselect({
  open,
  allowClear = true,
  className,
  placeholder,
  searchValue,
  value,
  label,
  errorText,
  loading,
  treeData,
  showSearch,
  isDisabled,
  dropdownLoading,
  customDropdown,
  dropdownStyle,
  treeNodeFilterProp,
  dropdownRender: dropdownRenderProp,
  dataTestId,
  onBlur,
  onSearch,
  onFocus,
  onClear,
  onChange,
  onClick,
  onDeselect,
  onDropdownVisibleChange,
  getPopupContainer,
  validationOptions,
}: Props) {
  const [searchTerm, setSearchTerm] = useState(searchValue ?? '');
  const { t } = useTranslation();

  function handleSearch(searchTerm: string): void {
    setSearchTerm(searchTerm);
    onSearch?.(searchTerm);
  }

  function localFilterTreeNode(searchValue: string, treeNode?: DefaultOptionType): boolean {
    const optionInLowerCase = treeNode?.title?.toString().toLowerCase();
    const searchValueInLowerCase = searchValue.toLowerCase();

    return !!optionInLowerCase?.includes(searchValueInLowerCase);
  }

  function dropdownRender(origin: ReactElement): ReactElement {
    if (dropdownLoading) return <DropdownPreloader />;
    if (dropdownRenderProp) return dropdownRenderProp(origin);
    if (customDropdown) return customDropdown;
    return origin;
  }

  function handleChange(changedValue: (string | number)[]): void {
    if (!validationOptions) {
      onChange?.(changedValue);
      return;
    }

    if (hasValue(validationOptions.maxCount) && changedValue.length > validationOptions.maxCount) {
      const message = label ? t('errors:limitExceeded') : t('errors:limitExceededWithLabel', { label });

      showWarningMessage(message);
    } else {
      onChange?.(changedValue);
    }
  }

  return (
    <div className={clsx(styles.wrapper, className)}>
      {!!label && <InputLabel text={label} />}
      <TreeSelect
        open={open}
        multiple={true}
        value={value ? value : []}
        data-testid={dataTestId}
        allowClear={allowClear}
        searchValue={searchValue || searchTerm}
        placeholder={loading ? t('common:pleaseWait') : placeholder || t('common:chooseValue')}
        className={clsx(styles.select, !!errorText && styles.select__error)}
        bordered={false}
        suffixIcon={
          loading ? (
            <div className={styles.loaderWrapper}>
              <Spin size="small" spinning />
            </div>
          ) : (
            <ArrowIcon className={styles.icon} fill="var(--contrast)" />
          )
        }
        showSearch={showSearch}
        disabled={isDisabled || loading}
        dropdownStyle={dropdownStyle}
        treeNodeFilterProp={treeNodeFilterProp}
        dropdownRender={dropdownRender}
        treeData={treeData}
        onClear={onClear}
        filterTreeNode={(inputValue, treeNode) => localFilterTreeNode(inputValue, treeNode as DefaultOptionType)}
        onFocus={onFocus}
        onBlur={onBlur}
        onSearch={handleSearch}
        onDeselect={onDeselect}
        onChange={handleChange}
        onClick={onClick}
        onDropdownVisibleChange={onDropdownVisibleChange}
        getPopupContainer={getPopupContainer}
        showArrow
      />
      {!!errorText && <InputError errorTextRaw={errorText} />}
    </div>
  );
});
